import * as v from 'valibot';
import {
  PreloadResource,
  ContentGUID,
  DocumentContent,
  VideoContent,
  TextContent,
  AudioContent,
  ScormContent,
  EnrollmentComponent,
  PreloadSettings,
  EnrollmentComponentSchema,
  SocialSharing,
  SocialSharingSchema,
  Flags,
  FlagsSchema,
} from '../shared/types/common.model';
import { ContentDeliveryClientError, ContentDeliveryClient } from './content-delivery-client';
import { safeParseJson } from '../shared/util/safe-parse-json';
import { ValiError } from 'valibot';
import { ENROLLMENT_COMPONENT_NOT_FOUND, INVALID_RESPONSE_FORMAT_ERROR, UNEXPECTED_ERROR } from './constants';
import { safeDecodeURIComponent } from '../shared/util/safe-decode-uri';

export class ContentDeliveryMonoRails implements ContentDeliveryClient {
  private preloadResource: PreloadResource;

  constructor() {
    this.fetchPreloadResource();
  }

  private fetchPreloadResource() {
    const preloadTag = document.querySelector('[preload-resource]');
    const preloadContent = preloadTag?.getAttribute('preload-resource');

    if (!preloadTag || !preloadContent) {
      throw new Error('Preload resource not found');
    }

    this.preloadResource = safeParseJson<PreloadResource>(safeDecodeURIComponent(preloadContent));
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getText(options: ContentGUID & { signal?: AbortSignal }): Promise<TextContent> {
    // Implement the method logic here
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getAudio(options: ContentGUID & { signal?: AbortSignal }): Promise<AudioContent> {
    // Implement the method logic here
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getDocument(options: ContentGUID & { signal?: AbortSignal }): Promise<DocumentContent> {
    // Implement the method logic here
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getVideo(options: ContentGUID & { signal?: AbortSignal }): Promise<VideoContent> {
    // Implement the method logic here
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getScorm(options: ContentGUID & { signal?: AbortSignal }): Promise<ScormContent> {
    // Implement the method logic here
  }

  /**
   * Fetch the course structure from the preload resource
   * @returns Validated course modules
   * @throws APIError if validation fails or no data found
   */
  async getCourseModules(): Promise<EnrollmentComponent[]> {
    try {
      const courseStructure: EnrollmentComponent[] = this.preloadResource?.enrollment_components?.data ?? [];

      const ResponseFormatSchema = v.pipe(
        v.array(EnrollmentComponentSchema),
        v.minLength(1, 'No enrollment components returned from api response')
      );
      return v.parse(ResponseFormatSchema, courseStructure);
    } catch (error) {
      if (error instanceof ValiError) {
        throw new ContentDeliveryClientError(INVALID_RESPONSE_FORMAT_ERROR, error.issues);
      }
      throw new ContentDeliveryClientError(UNEXPECTED_ERROR, error);
    }
  }

  /**
   * Fetch the enrollment from the preload resource
   */
  async getEnrollment() {
    return this.preloadResource?.enrollment.attributes;
  }

  /**
   * Fetch the enrollment component from the preload resource
   */
  async getEnrollmentComponent(): Promise<EnrollmentComponent> {
    const enrollmentComponent = this.preloadResource?.enrollment_component;

    if (!enrollmentComponent) {
      return Promise.reject(new ContentDeliveryClientError(ENROLLMENT_COMPONENT_NOT_FOUND, null));
    }

    if ('attributes' in enrollmentComponent) {
      return enrollmentComponent.attributes;
    }

    return enrollmentComponent;
  }

  /**
   * Fetch the flags from the preload resource
   */
  async getFlags() {
    try {
      const flags: Flags = this.preloadResource?.flags?.attributes ?? {};

      return v.parse(FlagsSchema, flags, { abortEarly: true });
    } catch (error) {
      if (error instanceof ValiError) {
        throw new ContentDeliveryClientError(INVALID_RESPONSE_FORMAT_ERROR, error.issues);
      }
      throw new ContentDeliveryClientError(UNEXPECTED_ERROR, error);
    }
  }

  /**
   * Fetch the on_description_page from the preload resource
   */
  async getOnDescriptionPage(): Promise<boolean> {
    return this.preloadResource?.on_description_page;
  }

  /**
   * Fetch the is_preview from the preload resource
   */
  async getIsPreview(): Promise<boolean> {
    return this.preloadResource?.is_preview;
  }

  /**
   * Fetch the settings from the preload resource
   */
  async getSettings(): Promise<PreloadSettings> {
    const preloadSettingsTag = document.querySelector('[preload-settings]');
    const preloadSettingsContent = preloadSettingsTag?.getAttribute('preload-settings');

    if (!preloadSettingsTag || !preloadSettingsContent) {
      throw new Error('Preload settings not found');
    }

    const preloadSettings: PreloadSettings = safeParseJson(safeDecodeURIComponent(preloadSettingsContent));
    return preloadSettings;
  }

  /**
   * Fetch the completion status from the preload resource
   */
  async getWasJustCompleted() {
    return Boolean(this.preloadResource?.was_just_completed);
  }

  /**
   * Fetch the social sharing settings from preload resource
   */
  async getSocialSharing() {
    try {
      if (!this.preloadResource?.social_sharing) {
        return null;
      }

      const socialSharing: SocialSharing = this.preloadResource?.social_sharing?.attributes;

      return v.parse(SocialSharingSchema, socialSharing, { abortEarly: true });
    } catch (error) {
      if (error instanceof ValiError) {
        throw new ContentDeliveryClientError(INVALID_RESPONSE_FORMAT_ERROR, error.issues);
      }
      throw new ContentDeliveryClientError(UNEXPECTED_ERROR, error);
    }
  }

  /**
   * Fetch the awarded badges from preload resource
   */
  async getAwardedBadges() {
    return this.preloadResource?.awarded_badges?.data;
  }

  /**
   * Fetch the next course on path from preload resource
   */
  async getNextCourseOnPath() {
    return this.preloadResource?.next_course_on_path_id;
  }

  /**
   * Flag in LuAnyWhere settings that hides the entire sidebar section.
   */
  async getEmbeddingSettings() {
    return {
      hide_module_sidebar: Boolean(this.preloadResource?.hide_module_sidebar),
      embedded_content: Boolean(this.preloadResource?.embedded_content),
      embedded_content_autoplay: Boolean(this.preloadResource?.embedded_content_autoplay),
    };
  }

  /**
   * Fetch scorm related settings
   */
  async getScormSettings() {
    return {
      tincan_completion_tracking: Boolean(this.preloadResource.tincan_completion_tracking),
    };
  }

  /**
   * Return Portal and lightweight feature flags
   */
  async getPortalFeatureFlags() {
    return {
      content_details_redesign_enabled: Boolean(this.preloadResource.content_details_redesign_enabled),
      concurrent_and_series_sessions: Boolean(this.preloadResource.concurrent_and_series_sessions),
    };
  }
}
